import './AssetTraffickingUploadModal.scss';
import { ChangeEvent, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { AssetTraffickingUploadSummary } from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { AlertDialog } from '@Components/AlertDialog';
import { BPMFileInput } from '@Components/BPMFileInput/BPMFileInput';
import { Button } from '@Components/Button';
import { Design, Intent, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { useSetError } from '../../redux/modals';
import { emailSelector } from '../../redux/user';
import { awaitJSON, BillingAutomationLambdaFetch } from '../../utils/fetch-utils';
import { BILLING_CORRECTION_JIRA_URL } from '../BillingAutomation/billing-constants';
import { recentMonthOptions } from '../BillingAutomation/billing-utils';

interface AssetTraffickingUploadModalProps {
  onHide: () => void;
}

const UPLOAD_MONTH_LOOKBACK = 12;

const monthOptions = recentMonthOptions(UPLOAD_MONTH_LOOKBACK, {
  startOffset: 1
});

const toBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const hasIssues = (summary: AssetTraffickingUploadSummary): boolean =>
  summary.omittedCids.length > 0 || summary.skippedSignedOffCids.length > 0;

const ResultDialogBody = ({ summary }: { summary: AssetTraffickingUploadSummary }) => (
  <>
    <div>
      {summary.clientsUpdated} client(s) updated, ${summary.totalAmount.toFixed(2)} total.
    </div>
    {summary.omittedCids.length > 0 && (
      <div>
        Omitted (unmatched ISCI, fix in creative_map and re-upload):{' '}
        {summary.omittedCids
          .map(o => `${o.cid} ($${o.amount.toFixed(2)}, ISCIs: ${o.unmatchedIscis.join(', ')})`)
          .join('; ')}
      </div>
    )}
    {summary.skippedSignedOffCids.length > 0 && (
      <div>
        Skipped clients - invoices already signed off this month (fees left unchanged):{' '}
        {summary.skippedSignedOffCids.map(s => `${s.cid} ($${s.amount.toFixed(2)})`).join('; ')}{' '}
        <a href={BILLING_CORRECTION_JIRA_URL} target='_blank' rel='noreferrer'>
          Submit a billing correction request
        </a>
        .
      </div>
    )}
  </>
);

export const AssetTraffickingUploadModal = ({ onHide }: AssetTraffickingUploadModalProps) => {
  const setError = useSetError();
  const uploadedBy = useSelector(emailSelector);
  const [billingMonth, setBillingMonth] = useState(monthOptions[0].value);
  const [file, setFile] = useState<File>();
  const [uploading, setUploading] = useState(false);
  const [resultSummary, setResultSummary] = useState<AssetTraffickingUploadSummary | null>(null);

  const upload = () => {
    if (!file) {
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const buffer = e?.target?.result;
        if (!(buffer instanceof ArrayBuffer)) {
          throw new Error('Failed to read file');
        }
        const res = await BillingAutomationLambdaFetch('/uploadAssetTraffickingFees', {
          method: 'POST',
          body: JSON.stringify({ billingMonth, fileBase64: toBase64(buffer), uploadedBy })
        });
        setResultSummary(await awaitJSON<AssetTraffickingUploadSummary>(res));
      } catch (err) {
        setError({
          message: err.message,
          title: 'Failed to upload asset trafficking fees',
          reportError: err
        });
      } finally {
        setUploading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <Modal show onHide={onHide} className='asset-trafficking-upload-modal'>
        <Modal.Header closeButton>
          <Modal.Title>Upload Asset Trafficking Fees</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            label='Billing Month'
            value={billingMonth}
            options={monthOptions}
            onChange={setBillingMonth}
            stretch={StretchMode.FIT}
          />
          <BPMFileInput
            id='asset-trafficking-file'
            design={Design.NEUTRAL}
            name='asset-trafficking-file'
            buttonText={file?.name ?? 'Upload Report'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0])}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={!file} loading={uploading} onClick={upload}>
            Upload
          </Button>
        </Modal.Footer>
      </Modal>
      <AlertDialog
        show={Boolean(resultSummary) && hasIssues(resultSummary!)}
        onHide={() => setResultSummary(null)}
        intent={Intent.WARNING}
        icon={<MdError size={40} />}
        title='Some Rows Were Not Uploaded'
        body={resultSummary && <ResultDialogBody summary={resultSummary} />}
      />
      <AlertDialog
        show={Boolean(resultSummary) && !hasIssues(resultSummary!)}
        onHide={() => setResultSummary(null)}
        intent={Intent.SUCCESS}
        icon={<MdCheckCircle size={40} />}
        title='Upload Successful'
        body={resultSummary && <ResultDialogBody summary={resultSummary} />}
      />
    </>
  );
};
